import { isDayjs } from "dayjs";
import dayjs from "./dayjsConfig";

export function formatAllDatesForSubmit<T>(values: T): T {
  const formatValue = (val: any, key?: string): any => {
    if (isDayjs(val) || val instanceof Date) {
      const d = dayjs(val);
      if (d.isValid()) {
        if (key && key.toLowerCase().includes("time")) {
          return d.format("HH:mm:ss");
        }
        if (key && key.toLowerCase().includes("date")) {
          return d.format("YYYY-MM-DD");
        }
        if (d.hour() === 0 && d.minute() === 0 && d.second() === 0) {
          return d.format("YYYY-MM-DD");
        }
        return d.format("HH:mm:ss");
      }
    } else if (typeof val === "string") {
      if (key && key.toLowerCase().includes("date")) {
        const parsedDate = dayjs(val, ["YYYY-MM-DD", "DD/MM/YYYY"], true);
        if (parsedDate.isValid()) {
          return parsedDate.format("YYYY-MM-DD");
        }
        return val;
      }
      const parsed = dayjs(
        val,
        ["hh:mm A", "h:mm A", "HH:mm", "HH:mm:ss"],
        true
      );
      if (parsed.isValid()) {
        return parsed.format("HH:mm:ss");
      }
    } else if (Array.isArray(val)) {
      return val.map((item) => formatValue(item, key));
    } else if (typeof val === "object" && val !== null) {
      const formattedObj: Record<string, any> = {};
      for (const k in val) {
        formattedObj[k] = formatValue(val[k], k);
      }
      return formattedObj;
    }
    return val;
  };

  return formatValue(values);
}
export function convertToDayjsForForm<T>(values: T): T {
  const convertValue = (val: any): any => {
    if (typeof val === "string") {
      const dateFormats = ["DD/MM/YYYY", "YYYY-MM-DD"];
      const timeFormats = ["HH:mm:ss", "YYYY-MM-DD HH:mm:ss", "hh:mm A"];

      for (const fmt of dateFormats) {
        const parsed = dayjs(val, fmt, true);
        if (parsed.isValid()) return parsed;
      }

      for (const fmt of timeFormats) {
        const parsed = dayjs(val, fmt, true);
        if (parsed.isValid()) return parsed.format("hh:mm A");
      }

      // Fallback for ISO 8601 date strings common in JSON
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(val)) {
        return dayjs(val);
      }

      return val;
    } else if (Array.isArray(val)) {
      return val.map((item) => convertValue(item));
    } else if (typeof val === "object" && val !== null) {
      // Map backend field names to frontend names if they exist
      // NOTE: only remap fields that differ between backend and frontend naming.
      // DO NOT remap start_time/end_time → registration_*_time because ticket data
      // already stores registration_start_time / registration_end_time directly.
      const mappedVal = { ...val };
      const mapping: Record<string, string> = {
        event_start_date: "start_date",
        event_start_time: "start_time",
        event_end_date: "end_date",
        event_end_time: "end_time",
        sales_start: "registration_start_date",
        sales_end: "registration_end_date",
      };

      for (const [oldKey, newKey] of Object.entries(mapping)) {
        if (oldKey in mappedVal) {
          mappedVal[newKey] = mappedVal[oldKey];
          // Note: we don't delete oldKey because TicketTable might still use them for display
        }
      }

      const convertedObj: Record<string, any> = {};
      for (const key in mappedVal) {
        convertedObj[key] = convertValue(mappedVal[key]);
      }
      return convertedObj;
    }
    return val;
  };

  return convertValue(values);
}

export function dateTimeDisplayFormat(
  dateStr: string | undefined,
  timeStr: string | undefined
): string {
  const dateTime = dayjs(`${dateStr} ${timeStr}`);
  if (!dateTime.isValid()) return "Invalid date/time";

  return `${formatDisplayDate(dateTime)}, ${formatDisplayTime(dateTime)}`;
}

export function formatDisplayDate(
  value: string | Date | dayjs.Dayjs | undefined | null
): string {
  const d = dayjs(value);
  if (!d.isValid()) return "—";
  return d.format("ddd, D MMM YYYY");
}

export function formatDisplayTime(
  value: string | Date | dayjs.Dayjs | undefined | null,
  parseFormats?: string[]
): string {
  const d = parseFormats
    ? dayjs(value as string, parseFormats, true)
    : dayjs(value);
  if (!d.isValid()) return "—";

  const minutes = d.minute();
  const hour = d.format("h");
  const meridiem = d.format(" A");

  return minutes === 0
    ? `${hour}${meridiem}`
    : `${hour}:${d.format("mm")}${meridiem}`;
}

export function formatDisplayDateTime(
  value: string | Date | dayjs.Dayjs | undefined | null
): string {
  const d = dayjs(value);
  if (!d.isValid()) return "—";
  return `${formatDisplayDate(d)}, ${formatDisplayTime(d)}`;
}
